import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';
import { TextBlock } from 'react-placeholder/lib/placeholders';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WordDocument from '../assets/word-document.svg';
import WordDocumentRed from '../assets/word-document-red.svg';
import IssueNew from '../assets/issue-new.svg';

import {
	makeStyles,
	Paper,
	Grid,
	Divider,
	Typography,
	Popover,
	Box,
	Tooltip,
	Link,
	withStyles
} from '@material-ui/core';

import Constants from "../utils/Constants";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import ActivityCategory from "./ActivityCategory";
import ActivityTopics from "./ActivityTopics";
import Timestamp from "./Timestamp";
import SourceIndicator from "./SourceIndicator";
import TrendingTopicLinks from "./TrendingTopicLinks";
import ActivityTitle from "./ActivityTitle";
import ActivityContent from "./ActivityContent";
import Translation from "./Translation";
import PostMedia from "./PostMedia";
import ActivityInfo from "./ActivityInfo";
import Comments from "./Comments";
import TipInfo from "./TipInfo";
import Analysis from './Analysis';
import { color } from 'highcharts';

const styles = makeStyles(theme => ({
	paper: {
		padding: theme.spacing(2.5),
		borderRadius: "15px",
		margin: theme.spacing(1)
	},

	topRow: {
		marginBottom: theme.spacing(1.5)
	},

	categoryItem: {
		paddingRight: theme.spacing(1),
		paddingTop: theme.spacing(0.5)
	},

	sourceIconItem: {
		paddingLeft: theme.spacing(1)
	},

	postMedia: {
		marginTop: theme.spacing(2)
	},

	activityInfo: {
		marginTop: theme.spacing(2)
	},

	commentsDivider: {
		marginTop: theme.spacing(2.5),
		marginBottom: theme.spacing(2.5)
	},

	actionsDivider: {
		marginTop: theme.spacing(4)
	},

	comments: {
		marginTop: theme.spacing(2.5)
	},

	viewPatternBtn: {
		color: theme.palette.primary.main,
		marginLeft: -theme.spacing(0.5)
	},

	divider: {
		margin: "10px -20px 20px -20px",
	},
	escalatedRejected: {
		color: theme.palette.error.main,
		fontWeight: "600",
		padding: "6px 8px",
		borderRadius: "32px",
		width: "fit-content",
		backgroundColor: "#FEFAF5", // Light red/pink background
		border: `1px solid #FDEAD7`,
		marginBottom: theme.spacing(1),
		cursor: 'pointer',
		display: 'inline-flex',
		alignItems: 'center',
	},
	escalated: {
		color: theme.palette.primary.main,
		fontWeight: "600",
		padding: "6px 8px",
		borderRadius: "32px",
		width: "fit-content",
		backgroundColor: Constants.COLORS.PRIMARY.LIGHT_BLUE,
		border: `1px solid ${Constants.COLORS.PRIMARY.LIGHT_CYAN}`,
		marginBottom: theme.spacing(1),
		cursor: 'pointer',
		display: 'inline-flex',
		alignItems: 'center',
	},
	chevron: {
		color: theme.palette.primary.main,
		paddingLeft: theme.spacing(1),
	},
	arrowRight: {
		color: '#ED4545',
		paddingLeft: theme.spacing(1),
	},
	popoverPaper: {
		width: 220,
		borderRadius: 12,
		padding: theme.spacing(2),
		marginTop: theme.spacing(1),
		borderBottom: `1px solid ${Constants.COLORS.SECONDARY.LIGHT_GREY}`,
	},
	statusPill: {
		backgroundColor: "#EFFBE6",
		color: "#387F00",
		padding: "4px 12px",
		borderRadius: "32px",
	},
	closedStatusPill: {
		backgroundColor: "#D9DCDD",
		color: "#000000",
		padding: "4px 12px",
		borderRadius: "32px",
	},
	viewCaseLink: {
		textDecoration: 'none',
		color: theme.palette.primary.main,
		fontWeight: 500,
	},
	logo: {
		marginRight: theme.spacing(1),
	},
	authorCasesPill: {
		color: theme.palette.error.main,
		fontSize: "12px",
		fontStyle: "normal",
		fontWeight: "600",
		lineHeight: "16px",
		padding: "6px 8px",
		borderRadius: "32px",
		width: "fit-content",
		backgroundColor: "#FDE8E8", // Light red/pink background
		border: `1px solid #F8B9B9`,
		marginRight: theme.spacing(1),
		cursor: 'pointer',
		display: 'inline-flex',
		alignItems: 'center',
	},
	authorChevron: {
		color: theme.palette.error.main,
		paddingLeft: theme.spacing(1),
	},
	bcm: {
		margin: 0,
		display: "flex",
		alignItems: "center",
		color: "#E81717",
		fontWeight: 700
	},
	reasonText: {
		paddingRight: theme.spacing(1),
		wordBreak: "break-word",
		overflowWrap: "anywhere",
	}
}));

const placeholderColor = Constants.COLORS.PLACEHOLDER_BG;
export const customPlaceholder = (
	<React.Fragment>
		<div style={{ display: "flex", justifyContent: "space-between" }}>
			<TextBlock rows={1} color={placeholderColor} style={{ width: "40%" }} />
			<TextBlock rows={1} color={placeholderColor} style={{ width: "15%" }} />
		</div>
		<TextBlock rows={1} color={placeholderColor} style={{ width: "85%", marginTop: 12 }} />
	</React.Fragment>
);

function PaperActivity(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedPill, setSelectedPill] = useState(null);

	let classes = styles(),
		topicsToBold = [],
		{ content, category, postMatches, time, published,
			trendingTopics, title, twitterRedacted,
			sourceId, sourceIcon, subSourceId, sourceName, sourceHandle,
			patch, history, extraHeaders, searchDetails,
			anchorText, comments, libraryTopics, topics,
			handleTranslateClick, translated, translatedTitle, language, errorMsg,
			imageUrl, videoUrl, iFrameVideo,
			authors, authorImage, authorLink, authorName, authorDesc,
			authorLoc, authorFollowing, authorFollowers, authorIp,
			fidoError, postLink, snapshot, revSnapshot,
			numberOfViewableComments, isActivityDeleted, addCommentHandler, viewPatternConfig,
			activityActions, tip, priorComments, /*activityId,*/ pattern, className, showAvatarRow,
			expandHeaderInfo, excludeHeaderInfoSent, elevation, id, previousMessages, alertInsights, activityCreatedAt, searchTerm, currentStatus, alertBCMCases, activity } = props,
		ready = content != null ? true : false,
		isTip = sourceName === 'tips',
		hasTipConcern = isTip && "concernOtherDescription" in tip && !!(tip.concernOtherDescription),
		patternDetected = category === Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.ID && Constants.PATTERN.isDetectionDate(pattern, published);

	if (trendingTopics) {
		topicsToBold = trendingTopics.reduce((result, topic) => {
			return topic.trending_topic.name.split(" | ");
		}, topicsToBold);
	}

	const renderPopoverContent = () => {
		const baseUrl = Constants.ENV.IS_LIVE
			? "https://bcm.navigate360.com/cases/"
			: "https://bcm-dev.navigate360.com/cases/";

		if (Object.keys(activity).length !== 0 && activity.author.alert_bcm_cases.length > 0 &&
			(activity.status === undefined || activity.status === 3) && selectedPill === 'student') {
			const authorAlertBCMCases = activity.author.alert_bcm_cases;
			return (
				<>
					<span>This student has the following open case{activity.author.alert_bcm_cases.length > 1 ? 's' : ''}.</span>
					{authorAlertBCMCases
						.filter(caseItem => caseItem.status !== 'Rejected' && caseItem.status !== 'Deleted') // skip rejected ones in detailed view
						.map((caseItem, index) => {


							const BCMurl = baseUrl + caseItem.case_id + "/overview";

							return (
								<Box key={caseItem.case_id || index} marginTop={1} borderTop="1px solid #eee" paddingTop={1} display="flex">
									<img src={WordDocumentRed} alt="document" className={classes.logo} />
									<a href={BCMurl} className={classes.viewCaseLink} target="_blank"><p className={classes.bcm}>
										Case CN-{caseItem.case_id}
										<FontAwesomeIcon
											icon={["far", "arrow-right"]}
											className={classes.arrowRight}
											title="chevron"
											size="md"
										/>
									</p>
									</a>
								</Box>
							);
						})}
				</>
			);
		}

		if (!Array.isArray(alertBCMCases)) return null;

		if (alertBCMCases.length === 0) {
			return <span>This alert was escalated but no case was created.</span>;
		}

		// Check if all cases are rejected
		const allRejected = alertBCMCases.every(caseItem => caseItem.status === 'Rejected');

		let deleted_message = alertBCMCases[0].deleted_message;
		const edited_by = alertBCMCases[0].edited_by;
		let editedOn = alertBCMCases[0].updated_at;
		editedOn = new Date(editedOn + "Z");

		const formatter = new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			timeZone: "America/New_York",   // or "UTC", "Europe/London", etc.
			timeZoneName: "short"
		});

		console.log(formatter.format(editedOn));

		if (allRejected) {
			const isLongMessage = deleted_message && deleted_message.length > 100;
			const truncatedMessage = isLongMessage
				? deleted_message.substring(0, 100) + "..."
				: deleted_message;

			return (
				<>
					<span>
						This alert was submitted to BCM, but case creation was declined.
					</span>
					<p style={{ paddingRight: 8 }}>
						<strong>Reason: </strong>
						{isLongMessage ? (
							<CustomTooltip
								title={
									<Box>
										<strong>Reason: </strong>
										{deleted_message}
									</Box>
								}
								placement="right"
							>
								<Box component="span" sx={{ cursor: "pointer" }} className={classes.reasonText}>
									{truncatedMessage}
									<Link
										component="span"
										underline="hover"
										sx={{ marginLeft: 0.5, cursor: "pointer", fontSize: "0.9rem" }}
									>
										view more
									</Link>
								</Box>
							</CustomTooltip>
						) : (
							<span>deleted_message</span>
						)}
					</p>
					<p>
						<strong>By:</strong> {edited_by} on {formatter.format(editedOn)}
					</p>
				</>
			);
		}

		return (
			<>
				<span>This alert was escalated to the following case{alertBCMCases.length > 1 ? 's' : ''}.</span>
				{alertBCMCases
					.filter(caseItem => caseItem.status !== 'Rejected') // skip rejected ones in detailed view
					.map((caseItem, index) => {

						const BCMurl = baseUrl + caseItem.case_id + "/overview";

						return (
							<Box key={caseItem.case_id || index} marginTop={1} borderTop="1px solid #eee" paddingTop={1}>
								<p><strong>Case Number:</strong> {caseItem.case_id}</p>
								<p><strong>Status:</strong> <span className={caseItem.status === 'Open' ? classes.statusPill : caseItem.status === 'Deleted' ? classes.authorCasesPill : classes.closedStatusPill}>{caseItem.status}</span></p>
								<p><strong>Assigned Team:</strong> {caseItem.team_name}</p>
								<p><strong>Team Leader:</strong> {caseItem.team_lead_name}</p>
								{caseItem.status != 'Deleted' ?
									<Box display="flex" justifyContent="flex-end" marginTop={2}>
										<img src={WordDocument} alt="document" className={classes.logo} />
										<a href={BCMurl} className={classes.viewCaseLink} target="_blank">
											View Case
											<FontAwesomeIcon icon={["far", "arrow-right"]} className={classes.chevron} title="chevron" size="xs" />
										</a>
									</Box>
									: null
								}
							</Box>
						)
					})}
			</>
		);
	};

	const renderEscalationPill = () => {

		if (Object.keys(activity).length === 0 || (activity.status !== undefined && activity.status !== 3)) return null;

		const authorBCMCases = activity.author.alert_bcm_cases;
		const authorAllRejected = authorBCMCases.every(caseItem => caseItem.status === 'Rejected' || caseItem.status === 'Deleted');

		const showStudentPill = authorBCMCases.length > 0 && !authorAllRejected;

		const StudentPill = (
			<Typography color="textPrimary" className={classes.authorCasesPill} variant="h3" onClick={(e) => handleClick(e, 'student')}>
				<img src={IssueNew} alt="issue" className={classes.logo} />
				Student with open case(s)
				<FontAwesomeIcon icon={["far", "arrow-right"]} className={classes.arrowRight} title="arrow" size='xs' />
			</Typography>
		);

		// If no cases or empty array
		if (activity.status === 3 && (!Array.isArray(alertBCMCases) || alertBCMCases.length === 0)) {
			return (
				<>{showStudentPill && StudentPill}
					<Typography color="textPrimary" className={classes.escalated} variant="h3" onClick={handleClick}>Escalated
						<FontAwesomeIcon icon={["far", "arrow-right"]} className={classes.chevron} title="chevron" size='xs' />
					</Typography></>
			);
		}

		// If all cases were rejected
		const allRejected = alertBCMCases.length > 0 && alertBCMCases.every(caseItem => caseItem.status === 'Rejected');

		if (allRejected) {
			return (
				<>{showStudentPill && StudentPill}
					<Typography
						color="textPrimary"
						className={classes.escalatedRejected}
						variant="h3"
						onClick={handleClick}
					>
						Escalation deleted in BCM
					</Typography>
				</>
			);
		}

		const hasCaseId = alertBCMCases.some(caseItem => !!caseItem.case_id);
		if (hasCaseId) {
			return (
				<>{showStudentPill && StudentPill}
					<Typography
						color="textPrimary"
						className={classes.escalated}
						variant="h3"
						onClick={handleClick}
					>
						Escalated to Case
						<FontAwesomeIcon
							icon={["far", "arrow-right"]}
							className={classes.chevron}
							title="chevron"
							size="xs"
						/>
					</Typography></>
			);
		}


		// Default escalation pill for escalated alerts
		if (activity.status) {
			return (
				<>{showStudentPill && StudentPill}
					<Typography color="textPrimary" className={classes.escalated} variant="h3" onClick={handleClick}>Escalated
						<FontAwesomeIcon icon={["far", "arrow-right"]} className={classes.chevron} title="chevron" size='xs' />
					</Typography></>
			);
		}

		return showStudentPill && StudentPill;
	};

	const handleClick = (event, pillType) => {
		setAnchorEl(event.currentTarget);
		setSelectedPill(pillType);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const popid = open ? 'escalated-popover' : undefined;


	return (
		<Paper
			id={`${id}`}
			elevation={elevation}
			className={`${classes.paper} ${className}`}>
			<ReactPlaceholder
				ready={ready}
				customPlaceholder={customPlaceholder}>
				<Grid
					container
					wrap="nowrap"
					alignItems="flex-start"
					justify="space-between"
					className={classes.topRow}>
					<Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
						<Grid
							container
							wrap="nowrap"
							justify="flex-start"
							alignItems="flex-start">
							<Grid item className={classes.categoryItem}>
								<ActivityCategory
									useDot
									category={category}
									pulsateAlerts={category === Constants.ACTIVITY.CATEGORIES.ALERTS.ID}
									colorOverride={patternDetected ? "orange" : null} />
							</Grid>
							<Grid item>
								<ActivityTopics
									topics={topics}
									matches={postMatches} />
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
						<Grid
							container
							wrap="nowrap"
							justify="flex-end"
							alignItems="center">
							<Grid item>
								<Timestamp style time={time} relative={false} format="ddd MMM Do, YYYY. h:mma" />
							</Grid>
							{sourceIcon ?
								<Grid item className={classes.sourceIconItem}>
									<SourceIndicator {...sourceIcon} />
								</Grid> : null
							}
						</Grid>
					</Grid>
				</Grid>
				<TrendingTopicLinks topics={trendingTopics} />
				<Divider className={classes.divider} />
				{renderEscalationPill()}
				<Popover
					id={popid}
					open={open}
					anchorEl={anchorEl}
					onClose={handleClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					PaperProps={{
						className: classes.popoverPaper,
					}}
				>
					<Typography className={classes.popoverContent}>
						{renderPopoverContent()}
					</Typography>
				</Popover>
				<ActivityTitle title={title} twitterRedacted={twitterRedacted} />

				<ActivityContent
					inlineReadMore
					patch={patch}
					sourceId={sourceId}
					subSourceId={subSourceId}
					sourceIcon={sourceIcon}
					authorName={authorName}
					history={history}
					time={time}
					content={content}
					anchorText={anchorText}
					priorComments={priorComments}
					category={category}
					boldWords={topicsToBold}
					libraryTopics={libraryTopics}
					twitterRedacted={twitterRedacted}
					originalContent={extraHeaders ? extraHeaders.content : null}
					previousMessages={previousMessages}
					extraHeaders={extraHeaders}
					searchTerm={searchTerm} />
				<Translation
					history={history}
					category={category}
					errorMsg={errorMsg}
					language={language}
					translated={translated}
					translatedTitle={translatedTitle}
					twitterRedacted={twitterRedacted}
					handleTranslateClick={handleTranslateClick} />
				{hasTipConcern ?
					<TipInfo {...tip} /> : null
				}
			</ReactPlaceholder>
			<ReactPlaceholder type='rect' ready={ready} style={{ height: 150, borderRadius: 0, marginTop: 12 }}>
				<PostMedia
					imageUrl={imageUrl}
					videoUrl={videoUrl}
					useIframeVideo={iFrameVideo}
					className={classes.postMedia} />
			</ReactPlaceholder>
			<ActivityInfo
				authors={authors}
				authorName={authorName}
				authorLink={authorLink}
				authorImage={authorImage}
				authorDesc={authorDesc}
				authorLoc={authorLoc}
				authorFollowing={authorFollowing}
				authorFollowers={authorFollowers}
				authorIp={authorIp}
				showAvatarRow={showAvatarRow}
				postLink={postLink}
				snapshot={snapshot}
				revSnapshot={revSnapshot}
				extraHeaders={extraHeaders}
				searchDetails={searchDetails}
				time={time}
				showTimestamp={isTip}
				twoLineSource
				fidoError={fidoError}
				sourceName={sourceName}
				sourceHandle={sourceHandle}
				sourceIcon={sourceIcon}
				sourceId={sourceId}
				subSourceId={subSourceId}
				twitterRedacted={twitterRedacted}
				className={classes.activityInfo}
				pattern={pattern}
				history={history}
				viewPatternConfig={viewPatternConfig}
				expandHeaderInfo={expandHeaderInfo}
				excludeHeaderInfoSent={excludeHeaderInfoSent} />
			<Divider className={classes.commentsDivider} />
			{authenticatedUser ? (
				<React.Fragment>
					{activityActions || (comments && comments.length) ? <Divider className={classes.commentsDivider} /> : null}
					{activityActions ?
						<React.Fragment>
							{activityActions}
							<Divider className={`${classes.commentsDivider} ${classes.actionsDivider}`} />
						</React.Fragment> : null
					}

					{
						alertInsights && alertInsights.length > 0 &&
						<React.Fragment>
							<Analysis ready={ready} alertInsights={alertInsights} createdAt={activityCreatedAt} />
							<Divider className={classes.commentsDivider} />
						</React.Fragment>
					}

					<Comments
						ready={ready}
						allowViewMore
						//actions={actions}
						comments={comments}
						isActivityDeleted={isActivityDeleted}
						addCommentHandler={addCommentHandler}
						numberToShow={numberOfViewableComments} />
				</React.Fragment>
			) : (
				<ReactPlaceholder type='textRow' ready={ready}>
					<Typography color="secondary">
						Sign in for comments and actions
					</Typography>
				</ReactPlaceholder>
			)}
		</Paper>
	);
}

const CustomTooltip = withStyles((theme) => ({
	tooltip: {
		borderRadius: 8,
		fontSize: "14px",
		padding: "8px 12px",
	},
}))(Tooltip);

PaperActivity.defaultProps = {
	elevation: 4
};

PaperActivity.propTypes = {
	history: PropTypes.object,
	//className: PropTypes.string,
	//activityId: PropTypes.number,
	title: PropTypes.string,
	content: PropTypes.string,
	patch: PropTypes.string,
	category: PropTypes.number,
	imageUrl: PropTypes.string,
	videoUrl: PropTypes.string,
	iFrameVideo: PropTypes.bool,
	postLink: PropTypes.string,
	snapshot: PropTypes.string,
	revSnapshot: PropTypes.string,
	authors: PropTypes.string,
	authorName: PropTypes.string,
	authorLink: PropTypes.string,
	authorImage: PropTypes.string,
	authorDesc: PropTypes.string,
	authorLoc: PropTypes.string,
	authorFollowing: PropTypes.number,
	authorFollowers: PropTypes.number,
	showAvatarRow: PropTypes.bool,
	sourceId: PropTypes.number,
	subSourceId: PropTypes.string,
	sourceName: PropTypes.string,
	sourceHandle: PropTypes.string,
	sourceIcon: PropTypes.object,
	time: PropTypes.string,
	published: PropTypes.string,
	anchorText: PropTypes.string,
	comments: PropTypes.array,
	priorComments: PropTypes.array,
	libraryTopics: PropTypes.array,
	topics: PropTypes.array,
	addCommentHandler: PropTypes.func,
	numberOfViewableComments: PropTypes.number,
	//actions: PropTypes.array,
	//showSnackbar: PropTypes.func,
	fidoError: PropTypes.bool,
	errorMsg: PropTypes.string,
	language: PropTypes.string,
	translated: PropTypes.string,
	translatedTitle: PropTypes.string,
	handleTranslateClick: PropTypes.func,
	trendingTopics: PropTypes.array,
	extraHeaders: PropTypes.object,
	searchDetails: PropTypes.object,
	//auditTrailRated: PropTypes.array,
	//rateHandler: PropTypes.func.isRequired,
	isActivityDeleted: PropTypes.bool,
	twitterRedacted: PropTypes.string,
	postMatches: PropTypes.array,
	tip: PropTypes.object,
	activityActions: PropTypes.object,
	pattern: PropTypes.object,
	activityId: PropTypes.number,
	className: PropTypes.string,
	expandHeaderInfo: PropTypes.bool,
	excludeHeaderInfoSent: PropTypes.bool,
	viewPatternConfig: PropTypes.object,
	elevation: PropTypes.number,
	id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default PaperActivity;